import { Grid, Button, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { LoadingButton } from '@mui/lab';
import { createAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { AutogiroMandatePostRequest } from '../models/autogiroMandatePostRequest';

type AutogiroMandateCreateModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export const AutogiroMandateCreateModal = (props: AutogiroMandateCreateModalProps) => {
  const { modalOpen, onClose, onSave } = props;

  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation(['common', 'groupMessage']);

  const autogiroMandateRequestSchema: Schema<AutogiroMandatePostRequest> = yup.object().shape({
    firstName: yup.string().required(t('common:validationMessages.required')),
    lastName: yup.string().required(t('common:validationMessages.required')),
    payerNr: yup.string().required(t('common:validationMessages.required')),
    personalIdentityNumber: yup
      .string()
      .matches(RegexHelper.personalNumber, t('invoice:createAutogiroMandate.personalIdentityNumberFormat'))
      .required(),
    owner_id: yup.number().min(1).required(),
    clearingNr: yup.string().matches(RegexHelper.clearingNr, t('invoice:createAutogiroMandate.clearingNrFormat')),
    accountNr: yup.string().matches(RegexHelper.accountNr, t('common:validationMessages.invalidFormat')),
    isSigned: yup.boolean().required(),
  });

  const formMethods = useForm<AutogiroMandatePostRequest>({
    resolver: yupResolver(autogiroMandateRequestSchema),
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      payerNr: '',
      personalIdentityNumber: '',
      owner_id: 0,
      clearingNr: '',
      accountNr: '',
      isSigned: false,
    },
  });

  const { handleSubmit, setValue, watch } = formMethods;

  const onSubmit = useCallback(
    async (data: AutogiroMandatePostRequest) => {
      setLoading(true);
      try {
        await createAutogiroMandate(data);
        if (isMounted()) {
          toast.success(<>{t('invoice:createAutogiroMandate.success')}</>);
          onSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onSave, t],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          open={modalOpen}
          padding={0}
          dialogTitle={t('invoice:createAutogiroMandate.modalHeader')}
          onClose={onHandleClose}
          onSave={onHandleSave}
          dialogContent={
            <FormLoaderWrapper loading={loading}>
              <Box sx={{ p: 2 }}>
                <GridWrapper>
                  <Grid item xs={12}>
                    <InvoiceGroups
                      callback={(ownId) => {
                        setValue('owner_id', ownId);
                      }}
                      placeholder={t('common:button.choose')}
                      emptyLabel={t('common:button.choose')}
                      autoSelect
                      ownerId={watch('owner_id')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      required
                      type='number'
                      name='personalIdentityNumber'
                      label={t('invoice:createAutogiroMandate.personalIdentityNumber')}
                      helperText={t('invoice:createAutogiroMandate.personalNumberHelperText')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      type='text'
                      required
                      name='payerNr'
                      label={t('invoice:createAutogiroMandate.payerNr')}
                      inputMode='numeric' // Ger numeriskt tangentbord på mobil
                      onChange={(e) => {
                        const sanitizedValue = e.target.value.replace(/\D/g, ''); // Tar bort alla icke-siffror
                        setValue('payerNr', sanitizedValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField required name='firstName' label={t('invoice:createAutogiroMandate.firstName')} />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField required name='lastName' label={t('invoice:createAutogiroMandate.lastName')} />
                  </Grid>
                  <Grid item xs={6}>
                    <Tooltip
                      title={
                        <span>
                          {t('invoice:createAutogiroMandateTooltip.clearingNrTooltip')}
                          <br />
                          <br />
                          <strong>{t('invoice:createAutogiroMandateTooltip.swedbank')}</strong>
                          {t('invoice:createAutogiroMandateTooltip.clearingNrSwedbank')}
                          <br />
                          <br />
                          <strong>{t('invoice:createAutogiroMandateTooltip.handelsbankens')}</strong>
                          {t('invoice:createAutogiroMandateTooltip.clearingNrHandelsbankens')}
                          <br />
                          <br />
                          <strong>{t('invoice:createAutogiroMandateTooltip.personAccNordea')}</strong>
                          {t('invoice:createAutogiroMandateTooltip.clearingNrPersonAccNordea')}
                          <br />
                          <br />
                          <strong>{t('invoice:createAutogiroMandateTooltip.personAccNordea')}</strong>
                          {t('invoice:createAutogiroMandateTooltip.clearingNrPersonAccNordeaNonPP')}
                          <br />
                          <br />
                          {t('invoice:createAutogiroMandateTooltip.clearingNrPlusGiro')}
                        </span>
                      }
                      arrow
                      placement='right'
                      aria-hidden
                    >
                      <span>
                        <ControlledTextField
                          type='text'
                          name='clearingNr'
                          label={t('invoice:createAutogiroMandate.clearingNr')}
                          inputMode='numeric' // Ger numeriskt tangentbord på mobil
                          onChange={(e) => {
                            const sanitizedValue = e.target.value.replace(/\D/g, ''); // Tar bort alla icke-siffror
                            setValue('clearingNr', sanitizedValue);
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      type='text'
                      name='accountNr'
                      label={t('invoice:createAutogiroMandate.accountNr')}
                      inputMode='numeric' // Ger numeriskt tangentbord på mobil
                      onChange={(e) => {
                        const sanitizedValue = e.target.value.replace(/\D/g, ''); // Tar bort alla icke-siffror
                        setValue('accountNr', sanitizedValue);
                      }}
                    />
                  </Grid>
                </GridWrapper>
              </Box>
            </FormLoaderWrapper>
          }
          dialogActions={
            <Grid container justifyContent='space-between'>
              <Grid item alignContent='center' ml={1}>
                <ControlledCheckboxNew name='isSigned' label='Har signerat medgivande via blankett' />
              </Grid>
              <Grid item>
                <Button sx={{ mr: 1 }} variant='outlined' disableElevation onClick={onHandleClose}>
                  {t('common:button.cancel')}
                </Button>
                <LoadingButton
                  disabled={watch('owner_id') === 0}
                  loading={loading}
                  type='submit'
                  variant='contained'
                  disableElevation
                  onClick={handleSubmitClick}
                >
                  {t('common:button.save')}
                </LoadingButton>
              </Grid>
            </Grid>
          }
        />
      </form>
    </FormProvider>
  );
};
